import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Stage, Layer, Image as KonvaImage, Line, Rect, Circle, Arrow, Text, Transformer, Group } from 'react-konva';
import { Save, X, Pencil, Square, CircleIcon, Type, Undo, Redo, Trash2, MousePointer2 } from 'lucide-react';
import useImage from 'use-image';
import Konva from 'konva';
import { api } from '../../../../api/client';
import './KonvaImageAnnotationEditor.css';

interface EnhancedKonvaEditorProps {
  src: string;
  onSave: (annotatedImageUrl: string) => void;
  onCancel: () => void;
  initialWidth?: number;
  initialHeight?: number;
  imageId?: string; // For refetching pre-signed URLs
}

type Tool = 'select' | 'pen' | 'rectangle' | 'circle' | 'arrow' | 'text' | 'eraser';

interface Shape {
  id: string;
  type: 'line' | 'rect' | 'circle' | 'arrow' | 'text';
  points?: number[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  radius?: number;
  text?: string;
  fill?: string;
  stroke: string;
  strokeWidth: number;
  backgroundColor?: string; // For text background
  borderColor?: string; // For text border
}

const EnhancedKonvaEditor: React.FC<EnhancedKonvaEditorProps> = ({
  src,
  onSave,
  onCancel,
  initialWidth = 800,
  initialHeight = 600,
  imageId,
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [image, imageStatus] = useImage(imageSrc, 'anonymous');
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [history, setHistory] = useState<Shape[][]>([[]]);
  const [historyStep, setHistoryStep] = useState(0);
  const [tool, setTool] = useState<Tool>('select');
  const [currentColor, setCurrentColor] = useState('#ef4444');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [editingText, setEditingText] = useState<string | null>(null);
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null);
  const [showTextBackground, setShowTextBackground] = useState(true);
  const [textBackgroundColor, setTextBackgroundColor] = useState('#000000');
  const [showTextBorder, setShowTextBorder] = useState(true);
  const [textBorderColor, setTextBorderColor] = useState('#ffffff');
  
  const stageRef = useRef<Konva.Stage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const refreshTriedRef = useRef(false);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Handle image load errors and refetch fresh pre-signed URLs
  useEffect(() => {
    if (imageStatus === 'failed' && !refreshTriedRef.current && imageId) {
      refreshTriedRef.current = true;
      const refetchImage = async () => {
        try {
          const normalizedId = imageId.replace(/^image:\/\//, '');
          const res = await api.post('/download_urls', { image_ids: [normalizedId] });
          const nextUrl: string | undefined = res?.data?.urls?.[normalizedId];
          if (nextUrl && nextUrl !== imageSrc) {
            setImageSrc(nextUrl);
            refreshTriedRef.current = false; // Allow retry with new URL
          }
        } catch (error) {
          console.error('Failed to refetch image URL:', error);
        }
      };
      refetchImage();
    }
  }, [imageStatus, imageId, imageSrc]);

  // Reset refresh flag when src changes
  useEffect(() => {
    setImageSrc(src);
    refreshTriedRef.current = false;
  }, [src]);

  // History management - using Konva's built-in serialization would be better
  const saveHistory = useCallback((newShapes: Shape[]) => {
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push([...newShapes]);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  }, [history, historyStep]);

  const undo = useCallback(() => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      setShapes([...history[historyStep - 1]]);
      setSelectedId(null);
    }
  }, [history, historyStep]);

  const redo = useCallback(() => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
      setShapes([...history[historyStep + 1]]);
      setSelectedId(null);
    }
  }, [history, historyStep]);

  const deleteSelected = useCallback(() => {
    if (selectedId) {
      const newShapes = shapes.filter(shape => shape.id !== selectedId);
      setShapes(newShapes);
      saveHistory(newShapes);
      setSelectedId(null);
    }
  }, [selectedId, shapes, saveHistory]);

  const clearAll = useCallback(() => {
    setShapes([]);
    saveHistory([]);
    setSelectedId(null);
  }, [saveHistory]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId && !editingText) {
          e.preventDefault();
          deleteSelected();
        }
      }
      if (e.key === 'Escape') {
        setSelectedId(null);
        setEditingText(null);
        setTextPosition(null);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, editingText, deleteSelected, undo, redo]);

  const handleShapeClick = useCallback((id: string) => {
    setSelectedId(id);
    setTool('select');
  }, []);

  // Handle double-click to edit text
  const handleTextDoubleClick = useCallback((shape: Shape) => {
    if (shape.type === 'text') {
      setTextPosition({ x: shape.x || 0, y: shape.y || 0 });
      setEditingText(shape.text || '');
      setSelectedId(shape.id);
    }
  }, []);

  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    // Deselect when clicking on empty space or background image
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.name() === 'background-image';
    if (clickedOnEmpty) {
      setSelectedId(null);
    }
  }, []);

  const handleMouseDown = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (tool === 'select') return;

    const stage = e.target.getStage();
    if (!stage) return;

    const point = stage.getPointerPosition();
    if (!point) return;

    if (tool === 'text') {
      setTextPosition(point);
      setEditingText('');
      return;
    }

    // Eraser: check if clicking on a shape to delete it
    if (tool === 'eraser') {
      const clickedShape = e.target;
      if (clickedShape !== stage) {
        const shapeId = clickedShape.id();
        if (shapeId) {
          const newShapes = shapes.filter(shape => shape.id !== shapeId);
          setShapes(newShapes);
          saveHistory(newShapes);
        }
      }
      setIsDrawing(true);
      return;
    }

    setIsDrawing(true);
    setSelectedId(null);

    const id = `shape-${Date.now()}`;
    let newShape: Shape;

    switch (tool) {
      case 'pen':
        newShape = {
          id,
          type: 'line',
          points: [point.x, point.y],
          stroke: currentColor,
          strokeWidth,
        };
        break;
      case 'rectangle':
        newShape = {
          id,
          type: 'rect',
          x: point.x,
          y: point.y,
          width: 0,
          height: 0,
          stroke: currentColor,
          strokeWidth,
        };
        break;
      case 'circle':
        newShape = {
          id,
          type: 'circle',
          x: point.x,
          y: point.y,
          radius: 0,
          stroke: currentColor,
          strokeWidth,
        };
        break;
      case 'arrow':
        newShape = {
          id,
          type: 'arrow',
          points: [point.x, point.y, point.x, point.y],
          stroke: currentColor,
          strokeWidth,
        };
        break;
      default:
        return;
    }

    setShapes([...shapes, newShape]);
  }, [tool, shapes, currentColor, strokeWidth, saveHistory]);

  const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || tool === 'select') return;

    const stage = e.target.getStage();
    if (!stage) return;

    const point = stage.getPointerPosition();
    if (!point) return;

    // Eraser: check if hovering over shapes to delete them
    if (tool === 'eraser') {
      const target = e.target;
      if (target !== stage) {
        const shapeId = target.id();
        if (shapeId) {
          const newShapes = shapes.filter(shape => shape.id !== shapeId);
          setShapes(newShapes);
        }
      }
      return;
    }

    const newShapes = [...shapes];
    const lastShape = newShapes[newShapes.length - 1];

    if (!lastShape) return;

    switch (lastShape.type) {
      case 'line':
        if (lastShape.points) {
          lastShape.points = [...lastShape.points, point.x, point.y];
        }
        break;
      case 'rect':
        if (lastShape.x !== undefined && lastShape.y !== undefined) {
          lastShape.width = point.x - lastShape.x;
          lastShape.height = point.y - lastShape.y;
        }
        break;
      case 'circle':
        if (lastShape.x !== undefined && lastShape.y !== undefined) {
          const dx = point.x - lastShape.x;
          const dy = point.y - lastShape.y;
          lastShape.radius = Math.sqrt(dx * dx + dy * dy);
        }
        break;
      case 'arrow':
        if (lastShape.points && lastShape.points.length >= 2) {
          lastShape.points = [lastShape.points[0], lastShape.points[1], point.x, point.y];
        }
        break;
    }

    setShapes(newShapes);
  }, [isDrawing, tool, shapes]);

  const handleMouseUp = useCallback(() => {
    if (isDrawing && tool !== 'eraser') {
      saveHistory(shapes);
    }
    setIsDrawing(false);
  }, [isDrawing, tool, shapes, saveHistory]);

  const handleTextSubmit = useCallback(() => {
    if (!editingText || editingText.trim() === '') {
      setEditingText(null);
      setTextPosition(null);
      return;
    }

    // Check if we're editing an existing text shape
    if (selectedId && shapes.find(s => s.id === selectedId && s.type === 'text')) {
      // Update existing text
      const newShapes = shapes.map(shape => 
        shape.id === selectedId && shape.type === 'text'
          ? { ...shape, text: editingText }
          : shape
      );
      setShapes(newShapes);
      saveHistory(newShapes);
      
      // Force transformer to update by re-selecting
      setTimeout(() => {
        if (transformerRef.current && layerRef.current) {
          const node = layerRef.current.findOne(`#${selectedId}`);
          if (node) {
            transformerRef.current.nodes([node]);
            transformerRef.current.forceUpdate();
            layerRef.current.batchDraw();
          }
        }
      }, 0);
    } else if (textPosition) {
      // Create new text
      const newShape: Shape = {
        id: `shape-${Date.now()}`,
        type: 'text',
        x: textPosition.x,
        y: textPosition.y,
        text: editingText,
        stroke: currentColor,
        strokeWidth: 0,
        fill: currentColor,
        backgroundColor: textBackgroundColor,
        borderColor: textBorderColor,
      };

      const newShapes = [...shapes, newShape];
      setShapes(newShapes);
      saveHistory(newShapes);
    }
    
    setEditingText(null);
    setTextPosition(null);
  }, [textPosition, editingText, selectedId, shapes, currentColor, textBackgroundColor, textBorderColor, saveHistory]);

  // Export using Konva's built-in export
  const exportImage = useCallback(async () => {
    if (!stageRef.current) return;

    setIsExporting(true);
    
    try {
      setSelectedId(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use Konva's built-in toDataURL with high quality
      const dataUrl = stageRef.current.toDataURL({ 
        pixelRatio: 2,
        mimeType: 'image/png',
        quality: 1
      });
      onSave(dataUrl);
    } catch (error) {
      console.error('Failed to export image:', error);
    } finally {
      setIsExporting(false);
    }
  }, [onSave]);

  // Update transformer
  useEffect(() => {
    if (!transformerRef.current || !layerRef.current) return;

    const transformer = transformerRef.current;
    const layer = layerRef.current;

    if (selectedId) {
      const node = layer.findOne(`#${selectedId}`);
      if (node) {
        transformer.nodes([node]);
        transformer.getLayer()?.batchDraw();
      }
    } else {
      transformer.nodes([]);
    }
  }, [selectedId]);

  const tools = [
    { id: 'select', name: 'Select', Component: MousePointer2 },
    { id: 'pen', name: 'Pen', Component: Pencil },
    { id: 'rectangle', name: 'Rectangle', Component: Square },
    { id: 'circle', name: 'Circle', Component: CircleIcon },
    { id: 'arrow', name: 'Arrow', Component: null, icon: '➡️' },
    { id: 'text', name: 'Text', Component: Type },
  ];

  // Handle color change - update selected shape if one is selected
  const handleColorChange = useCallback((color: string) => {
    setCurrentColor(color);
    
    // If a shape is selected, update its color
    if (selectedId) {
      const newShapes = shapes.map(shape => {
        if (shape.id === selectedId) {
          return {
            ...shape,
            stroke: color,
            fill: shape.type === 'text' ? color : shape.fill, // Update fill for text
          };
        }
        return shape;
      });
      setShapes(newShapes);
      saveHistory(newShapes);
    }
  }, [selectedId, shapes, saveHistory]);

  // Handle background color change - update selected text shape
  const handleBackgroundColorChange = useCallback((color: string) => {
    setTextBackgroundColor(color);
    
    // If a text shape is selected, update its background color
    if (selectedId) {
      const newShapes = shapes.map(shape => {
        if (shape.id === selectedId && shape.type === 'text') {
          return {
            ...shape,
            backgroundColor: color,
          };
        }
        return shape;
      });
      setShapes(newShapes);
      saveHistory(newShapes);
    }
  }, [selectedId, shapes, saveHistory]);

  // Handle border color change - update selected text shape
  const handleBorderColorChange = useCallback((color: string) => {
    setTextBorderColor(color);
    
    // If a text shape is selected, update its border color
    if (selectedId) {
      const newShapes = shapes.map(shape => {
        if (shape.id === selectedId && shape.type === 'text') {
          return {
            ...shape,
            borderColor: color,
          };
        }
        return shape;
      });
      setShapes(newShapes);
      saveHistory(newShapes);
    }
  }, [selectedId, shapes, saveHistory]);

  // Handle stroke width change - update selected shape if one is selected
  const handleStrokeWidthChange = useCallback((width: number) => {
    setStrokeWidth(width);
    
    // If a shape is selected, update its stroke width
    if (selectedId) {
      const newShapes = shapes.map(shape => {
        if (shape.id === selectedId) {
          return {
            ...shape,
            strokeWidth: width,
          };
        }
        return shape;
      });
      setShapes(newShapes);
      saveHistory(newShapes);
    }
  }, [selectedId, shapes, saveHistory]);

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-2 sm:p-4"
      style={{ 
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)'
      }}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="rounded-lg p-4 sm:p-6 w-full max-w-7xl max-h-[200vh] overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(to bottom, #1e293b, #0f172a)',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          color: 'white'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white">
              Annotate Image
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Use the tools below to annotate your image
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800 hover:border-gray-500 transition-colors flex items-center gap-2"
            >
              <X size={16} />
              Cancel
            </button>
            <button
              onClick={exportImage}
              disabled={isExporting || imageStatus === 'loading' || imageStatus === 'failed'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save
                </>
              )}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {imageStatus === 'loading' && (
          <div className="flex-1 flex justify-center items-center overflow-auto bg-gray-900 rounded-lg p-4">
            <div className="text-center">
              <svg className="animate-spin h-16 w-16 text-blue-500 mx-auto mb-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-300 text-lg font-medium">Loading image...</p>
              <p className="text-gray-500 text-sm mt-2">Please wait while we prepare your image for annotation</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {imageStatus === 'failed' && (
          <div className="flex-1 flex justify-center items-center overflow-auto bg-gray-900 rounded-lg p-4">
            <div className="text-center">
              <div className="text-red-500 mb-6">
                <X size={64} className="mx-auto" />
              </div>
              <p className="text-gray-300 mb-3 text-lg font-medium">Failed to load image</p>
              <p className="text-sm text-gray-500">The image URL may have expired. Please close and try again.</p>
            </div>
          </div>
        )}

        {/* Canvas */}
        {imageStatus === 'loaded' && (
        <div className="flex-1 flex justify-center items-center overflow-auto bg-gray-900 rounded-lg p-4">
          <Stage
            ref={stageRef}
            width={initialWidth}
            height={initialHeight}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={handleStageClick}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          >
            <Layer ref={layerRef}>
              {/* Background Image */}
              {image && <KonvaImage name="background-image" image={image} width={initialWidth} height={initialHeight} />}
              
              {/* Shapes - Konva handles all the rendering */}
              {shapes.map((shape) => {
                // Separate key from other props to avoid React warning
                const shapeProps = {
                  id: shape.id,
                  onClick: () => handleShapeClick(shape.id),
                  onDblClick: shape.type === 'text' ? () => handleTextDoubleClick(shape) : undefined,
                  draggable: tool === 'select',
                };

                switch (shape.type) {
                  case 'line': {
                    // Calculate bounding box for the line
                    const linePoints = shape.points || [];
                    if (linePoints.length < 2) return null;
                    
                    const lineXs = linePoints.filter((_, i) => i % 2 === 0);
                    const lineYs = linePoints.filter((_, i) => i % 2 === 1);
                    const lineMinX = Math.min(...lineXs);
                    const lineMaxX = Math.max(...lineXs);
                    const lineMinY = Math.min(...lineYs);
                    const lineMaxY = Math.max(...lineYs);
                    
                    return (
                      <Group
                        key={shape.id}
                        {...shapeProps}
                      >
                        {/* Invisible rectangle for easier clicking/dragging */}
                        <Rect
                          x={lineMinX}
                          y={lineMinY}
                          width={lineMaxX - lineMinX}
                          height={lineMaxY - lineMinY}
                          fill="transparent"
                          listening={true}
                        />
                        {/* Actual line */}
                        <Line
                          points={shape.points || []}
                          stroke={shape.stroke}
                          strokeWidth={shape.strokeWidth}
                          tension={0.5}
                          lineCap="round"
                          lineJoin="round"
                          listening={false}
                        />
                      </Group>
                    );
                  }
                  case 'rect':
                    return (
                      <Rect
                        key={shape.id}
                        {...shapeProps}
                        x={shape.x || 0}
                        y={shape.y || 0}
                        width={shape.width || 0}
                        height={shape.height || 0}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                      />
                    );
                  case 'circle':
                    return (
                      <Circle
                        key={shape.id}
                        {...shapeProps}
                        x={shape.x || 0}
                        y={shape.y || 0}
                        radius={shape.radius || 0}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                      />
                    );
                  case 'arrow': {
                    // Calculate bounding box for the arrow
                    const arrowPoints = shape.points || [];
                    if (arrowPoints.length < 4) return null;
                    
                    const arrowXs = [arrowPoints[0], arrowPoints[2]];
                    const arrowYs = [arrowPoints[1], arrowPoints[3]];
                    const arrowMinX = Math.min(...arrowXs);
                    const arrowMaxX = Math.max(...arrowXs);
                    const arrowMinY = Math.min(...arrowYs);
                    const arrowMaxY = Math.max(...arrowYs);
                    
                    return (
                      <Group
                        key={shape.id}
                        {...shapeProps}
                      >
                        {/* Invisible rectangle for easier clicking/dragging */}
                        <Rect
                          x={arrowMinX}
                          y={arrowMinY}
                          width={arrowMaxX - arrowMinX || 10}
                          height={arrowMaxY - arrowMinY || 10}
                          fill="transparent"
                          listening={true}
                        />
                        {/* Actual arrow */}
                        <Arrow
                          points={shape.points || []}
                          stroke={shape.stroke}
                          strokeWidth={shape.strokeWidth}
                          pointerLength={10}
                          pointerWidth={10}
                          listening={false}
                        />
                      </Group>
                    );
                  }
                  case 'text': {
                    const fontSize = 24;
                    const padding = 6;
                    
                    // Create a temporary text element to measure actual width
                    const tempText = new Konva.Text({
                      text: shape.text || '',
                      fontSize: fontSize,
                      fontFamily: 'Arial'
                    });
                    const textWidth = tempText.width();
                    
                    return (
                      <Group
                        key={shape.id}
                        {...shapeProps}
                      >
                        {/* Background rectangle and/or border */}
                        {(showTextBackground || showTextBorder) && (
                          <Rect
                            x={(shape.x || 0) - padding}
                            y={(shape.y || 0) - padding}
                            width={textWidth + padding * 2}
                            height={fontSize + padding * 2}
                            fill={showTextBackground ? (shape.backgroundColor || textBackgroundColor) : 'transparent'}
                            opacity={showTextBackground ? 0.85 : 1}
                            cornerRadius={4}
                            stroke={showTextBorder ? (shape.borderColor || textBorderColor) : undefined}
                            strokeWidth={showTextBorder ? 2 : 0}
                            listening={true}
                          />
                        )}
                        {/* Text */}
                        <Text
                          x={shape.x || 0}
                          y={shape.y || 0}
                          text={shape.text || ''}
                          fontSize={fontSize}
                          fill={shape.fill || shape.stroke}
                          fontFamily="Arial"
                          listening={!showTextBackground && !showTextBorder}
                        />
                      </Group>
                    );
                  }
                  default:
                    return null;
                }
              })}
              
              {/* Konva's built-in Transformer */}
              {tool === 'select' && <Transformer ref={transformerRef} />}
            </Layer>
          </Stage>
        </div>
        )}

        {/* Text Input */}
        {imageStatus === 'loaded' && textPosition && (
          <div className="border-t border-gray-700 p-4 bg-gray-800/50 mt-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-300">Enter text:</span>
              <input
                type="text"
                value={editingText || ''}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTextSubmit();
                  else if (e.key === 'Escape') {
                    setEditingText(null);
                    setTextPosition(null);
                  }
                }}
                autoFocus
                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                placeholder="Type your text..."
              />
              <button onClick={handleTextSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                {selectedId && shapes.find(s => s.id === selectedId && s.type === 'text') ? 'Save' : 'Add Text'}
              </button>
              <button onClick={() => { setEditingText(null); setTextPosition(null); }} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        {imageStatus === 'loaded' && (
        <div className="border-t border-gray-700 p-3 bg-gray-800/50 mt-4">
          <div className="flex items-center gap-3">
            {/* Tools */}
            <div className="flex gap-1">
              {tools.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTool(t.id as Tool)}
                  className={`p-2 rounded transition-colors ${
                    tool === t.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  title={t.name}
                >
                  {t.Component ? <t.Component size={18} /> : <span>{t.icon}</span>}
                </button>
              ))}
            </div>

            <div className="h-8 w-px bg-gray-600" />

            {/* Color Pickers */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-400 whitespace-nowrap">
                  {selectedId && shapes.find(s => s.id === selectedId)?.type === 'text' ? 'Text Color:' : 'Color:'}
                </label>
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-2 border-gray-600 hover:border-gray-400"
                  title="Choose color"
                />
              </div>

              {/* Background Color - only show for text shapes */}
              {selectedId && shapes.find(s => s.id === selectedId)?.type === 'text' && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400 whitespace-nowrap flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={showTextBackground}
                        onChange={(e) => setShowTextBackground(e.target.checked)}
                        className="w-4 h-4"
                      />
                      Background:
                    </label>
                    <input
                      type="color"
                      value={textBackgroundColor}
                      onChange={(e) => handleBackgroundColorChange(e.target.value)}
                      disabled={!showTextBackground}
                      className="w-8 h-8 rounded cursor-pointer border-2 border-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Choose background color"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400 whitespace-nowrap flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={showTextBorder}
                        onChange={(e) => setShowTextBorder(e.target.checked)}
                        className="w-4 h-4"
                      />
                      Border:
                    </label>
                    <input
                      type="color"
                      value={textBorderColor}
                      onChange={(e) => handleBorderColorChange(e.target.value)}
                      disabled={!showTextBorder}
                      className="w-8 h-8 rounded cursor-pointer border-2 border-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Choose border color"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="h-8 w-px bg-gray-600" />

            {/* Size */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400 whitespace-nowrap">Change Size:</span>
              <div className="flex gap-1">
                {[
                  { label: 'xs', value: 1 },
                  { label: 'sm', value: 3 },
                  { label: 'md', value: 5 },
                  { label: 'xl', value: 8 }
                ].map((size) => (
                  <button
                    key={size.value}
                    onClick={() => handleStrokeWidthChange(size.value)}
                    className={`px-2 py-1 rounded transition-colors text-sm ${
                      strokeWidth === size.value ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title={`${size.value}px`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-8 w-px bg-gray-600" />

            {/* Actions */}
            <div className="flex gap-1">
              <button onClick={undo} disabled={historyStep === 0} className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Undo">
                <Undo size={18} />
              </button>
              <button onClick={redo} disabled={historyStep === history.length - 1} className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Redo">
                <Redo size={18} />
              </button>
              <button onClick={deleteSelected} disabled={!selectedId} className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Delete">
                <Trash2 size={18} />
              </button>
              <button onClick={clearAll} disabled={shapes.length === 0} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Clear All">
                Clear All
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EnhancedKonvaEditor;
